import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { cacheManager } from '@/utils/cache-manager';
import { errorTracker } from './use-subscription-auto-refresh';

interface SubscriptionOptions {
  channel: string;
  event: string;
  schema?: string;
  table?: string;
  filter?: string;
  callback: (payload: any) => void;
  debounceMs?: number;
  autoRefreshOnError?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

export function useSupabaseSubscription(options: SubscriptionOptions) {
  const subscriptionRef = useRef<any>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialized = useRef(false);
  const retryCount = useRef(0);
  const isRetrying = useRef(false);
  const lastSuccessfulConnection = useRef<number>(0);
  const connectionAttempts = useRef(0);
  const { 
    channel, 
    event, 
    schema = 'public', 
    table, 
    filter, 
    callback, 
    debounceMs = 500,
    autoRefreshOnError = true,
    maxRetries = 3,
    retryDelay = 2000
  } = options;

  // Função para limpar timeouts
  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Função para limpar cache de forma mais inteligente
  const clearRelatedCache = useCallback(() => {
    if (table) {
      const cacheKey = `supabase_${schema}_${table}`;
      const cachedData = cacheManager.getCache(cacheKey);
      
      // Só limpar se o cache for muito antigo (> 10 minutos) ou se houver problemas
      if (cachedData && Date.now() - cachedData.timestamp > 10 * 60 * 1000) {
        cacheManager.removeCache(cacheKey);
        console.log(`Cache antigo limpo para ${cacheKey}`);
      }
    }
  }, [table, schema]);

  // Função para verificar se deve tentar reconectar
  const shouldRetry = useCallback((status: string): boolean => {
    // Não tentar reconectar se já está tentando
    if (isRetrying.current) {
      return false;
    }

    // Não tentar reconectar se excedeu o número máximo de tentativas
    if (retryCount.current >= maxRetries) {
      return false;
    }

    // Só tentar reconectar para erros específicos
    if (status !== 'CHANNEL_ERROR' && status !== 'CLOSED') {
      return false;
    }

    // Verificar se a última conexão bem-sucedida foi há muito tempo
    const timeSinceLastSuccess = Date.now() - lastSuccessfulConnection.current;
    if (timeSinceLastSuccess < 30000) { // 30 segundos
      console.log(`Conexão recente bem-sucedida, aguardando antes de tentar reconectar`);
      return false;
    }

    return true;
  }, [maxRetries]);

  // Função para lidar com erros de subscrição de forma mais inteligente
  const handleSubscriptionError = useCallback(async (status: string, error?: any) => {
    console.warn(`Erro na subscrição ${channel}:`, status, error);
    
    // Registrar erro no rastreador global
    errorTracker.registerError(channel, status);
    
    // Verificar se deve tentar reconectar
    if (!shouldRetry(status)) {
      return;
    }

    // Marcar como tentando reconectar
    isRetrying.current = true;
    retryCount.current++;
    
    console.log(`Tentativa de reconexão ${retryCount.current}/${maxRetries} para ${channel}`);
    
    // Limpar cache relacionado de forma inteligente
    clearRelatedCache();
    
    // Calcular delay com backoff exponencial mais conservador
    const delay = Math.min(retryDelay * Math.pow(1.3, retryCount.current - 1), 30000);
    
    retryTimeoutRef.current = setTimeout(async () => {
      try {
        // Verificar se ainda deve tentar reconectar
        if (retryCount.current >= maxRetries) {
          console.log(`Máximo de tentativas atingido para ${channel}`);
          isRetrying.current = false;
          return;
        }

        // Limpar subscrição atual
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }

        // Aguardar um pouco antes de tentar criar nova subscrição
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Tentar criar nova subscrição
        const newSubscription = createSubscription();
        if (newSubscription) {
          subscriptionRef.current = newSubscription;
          retryCount.current = 0;
          isRetrying.current = false;
          lastSuccessfulConnection.current = Date.now();
          console.log(`Reconexão bem-sucedida para ${channel}`);
          
          // Resetar erros do canal no rastreador global
          errorTracker.resetChannelErrors(channel);
        } else {
          isRetrying.current = false;
        }
      } catch (error) {
        console.error(`Falha na reconexão para ${channel}:`, error);
        isRetrying.current = false;
      }
    }, delay);
  }, [channel, maxRetries, retryDelay, clearRelatedCache, shouldRetry]);



  // Função para criar subscrição com melhor tratamento de erros
  const createSubscription = useCallback(() => {
    try {
      connectionAttempts.current++;
      
      const subscription = (supabase as any)
        .channel(channel)
        .on('postgres_changes', { 
          event, 
          schema, 
          table,
          filter 
        }, (payload: any) => {
          console.log(`Evento ${event} detectado em ${table}:`, payload);
          
          // Aplicar debounce se especificado
          if (debounceMs > 0) {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            
            timeoutRef.current = setTimeout(() => {
              callback(payload);
            }, debounceMs);
          } else {
            callback(payload);
          }
        })
        .subscribe((status: string) => {
          console.log(`Subscrição ${channel} status:`, status);
          
          // Tratar erros de subscrição
          if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
            handleSubscriptionError(status);
          } else if (status === 'SUBSCRIBED') {
            // Reset contador de tentativas quando conectar com sucesso
            retryCount.current = 0;
            isRetrying.current = false;
            lastSuccessfulConnection.current = Date.now();
            connectionAttempts.current = 0;
            console.log(`Subscrição ${channel} estabelecida com sucesso`);
          } else if (status === 'TIMED_OUT') {
            console.warn(`Timeout na subscrição ${channel}, tentando reconectar...`);
            handleSubscriptionError('TIMED_OUT');
          }
        });

      return subscription;
    } catch (error) {
      console.error(`Erro ao criar subscrição ${channel}:`, error);
      handleSubscriptionError('ERROR', error);
      return null;
    }
  }, [channel, event, schema, table, filter, callback, debounceMs, handleSubscriptionError]);

  // Efeito para inicializar a subscrição
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Aguardar um pouco antes de criar a primeira subscrição
    const initTimeout = setTimeout(() => {
      const subscription = createSubscription();
      if (subscription) {
        subscriptionRef.current = subscription;
      }
    }, 1000);

    return () => {
      clearTimeout(initTimeout);
      clearTimeouts();
      
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      
      retryCount.current = 0;
      isRetrying.current = false;
      connectionAttempts.current = 0;
    };
  }, [createSubscription, clearTimeouts]);

  // Função para forçar reconexão manual
  const forceReconnect = useCallback(async () => {
    console.log(`Forçando reconexão para ${channel}...`);
    
    // Resetar contadores
    retryCount.current = 0;
    isRetrying.current = false;
    connectionAttempts.current = 0;
    
    // Limpar timeouts
    clearTimeouts();
    
    // Limpar subscrição atual
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Aguardar um pouco antes de tentar reconectar
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Criar nova subscrição
    const newSubscription = createSubscription();
    if (newSubscription) {
      subscriptionRef.current = newSubscription;
    }
  }, [channel, createSubscription, clearTimeouts]);

  // Função para limpar subscrição
  const unsubscribe = useCallback(() => {
    clearTimeouts();
    
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    
    retryCount.current = 0;
    isRetrying.current = false;
    connectionAttempts.current = 0;
  }, [clearTimeouts]);

  return { 
    unsubscribe, 
    forceReconnect, 
    retryCount: retryCount.current,
    isRetrying: isRetrying.current,
    connectionAttempts: connectionAttempts.current
  };
}

// Hook específico para autenticação com melhor tratamento de erros
export function useAuthSubscription(callback: (event: string, session: any) => void) {
  const subscriptionRef = useRef<any>(null);
  const isInitialized = useRef(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const setupAuthSubscription = () => {
      try {
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          
          callback(event, session);
        });

        subscriptionRef.current = subscription;
      } catch (error) {
        console.error('Erro ao configurar subscrição de auth:', error);
        
        // Tentar novamente após um delay
        retryTimeoutRef.current = setTimeout(setupAuthSubscription, 5000);
      }
    };

    setupAuthSubscription();

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [callback]);

  return { subscription: subscriptionRef.current };
}
